/**
 * Environment Configuration
 *
 * Centralizes environment-specific settings and provides smart defaults
 * for different deployment stages (dev, personal, prod).
 */

// Environment detection
export const NODE_ENV = process.env.NODE_ENV || 'development';
export const REACT_APP_STAGE = process.env.REACT_APP_STAGE || 'dev';

// API Configuration with stage-specific defaults
// NOTE: These are local-dev fallback URLs only. Production builds MUST set
// REACT_APP_API_BASE_URL via CI — see deploy-frontend-prod.yml.
// Updated dev URL to match current dev stack (2026-05-20)
const API_BASE_URLS = {
  dev: 'https://e44ti2amme.execute-api.us-east-2.amazonaws.com/dev',
  prod: 'https://g95uk6g1ke.execute-api.us-east-2.amazonaws.com/prod',
  production: 'https://g95uk6g1ke.execute-api.us-east-2.amazonaws.com/prod',
  personal: 'https://dzr3tzpml4.execute-api.us-east-2.amazonaws.com/personal'
};

// S3 Configuration
const S3_BASE_URLS = {
  dev: 'https://naultic-mogulplex-assets-dev.s3.us-east-2.amazonaws.com',
  prod: 'https://naultic-mogulplex-assets.s3.us-east-2.amazonaws.com',
  personal: 'https://naultic-mogulplex-assets-dev.s3.us-east-2.amazonaws.com'
};

// Get API base URL with fallback logic
export const getApiBaseUrl = () => {
  // 1. Check for explicit environment variable (always preferred)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 2. Production builds MUST have an explicit URL from CI to prevent drift
  if (NODE_ENV === 'production' && (REACT_APP_STAGE === 'prod' || REACT_APP_STAGE === 'production')) {
    console.error(
      'REACT_APP_API_BASE_URL is not set for a production build. ' +
      'Production deploys must inject the API URL from CloudFormation outputs.'
    );
  }

  // 3. Use stage-specific default (local dev only)
  const stageUrl = API_BASE_URLS[REACT_APP_STAGE];
  if (stageUrl) {
    return stageUrl;
  }

  // 4. Fallback to dev
  console.warn(`Unknown stage '${REACT_APP_STAGE}', falling back to dev API URL`);
  return API_BASE_URLS.dev;
};

// Get S3 base URL with fallback logic
export const getS3BaseUrl = () => {
  // 1. Check for explicit environment variable
  if (process.env.REACT_APP_S3_BASE_URL) {
    return process.env.REACT_APP_S3_BASE_URL;
  }

  // 2. Use stage-specific default
  const stageUrl = S3_BASE_URLS[REACT_APP_STAGE];
  if (stageUrl) {
    return stageUrl;
  }

  // 3. Fallback to dev
  return S3_BASE_URLS.dev;
};

// Configuration object
export const config = {
  // Environment
  isDevelopment: NODE_ENV === 'development',
  isProduction: NODE_ENV === 'production',
  isPersonal: REACT_APP_STAGE === 'personal',
  isDev: REACT_APP_STAGE === 'dev',

  // Banner visibility
  shouldShowBanner: REACT_APP_STAGE !== 'prod' && REACT_APP_STAGE !== 'production',

  // Banner content
  bannerConfig: {
    dev: {
      bg: 'orange.500',
      color: 'black',
      text: '⚠️ DEVELOPMENT ENVIRONMENT'
    },
    personal: {
      bg: 'purple.600',
      color: 'white',
      text: '👤 PERSONAL STACK'
    }
  },

  // API
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: 30000, // 30 seconds
    retries: 3
  },

  // Assets
  assets: {
    s3BaseUrl: getS3BaseUrl(),
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
    maxCacheSize: 100 * 1024 * 1024, // 100MB
    supportedFormats: ['glb', 'gltf'],
    supportedImageFormats: ['jpg', 'jpeg', 'png', 'webp'],
    supportedAudioFormats: ['mp3', 'wav'],
    supportedVideoFormats: ['mp4', 'mov']
  },

  // Upload limits
  upload: {
    maxFileSize: {
      image: 10 * 1024 * 1024, // 10MB
      audio: 50 * 1024 * 1024, // 50MB
      video: 100 * 1024 * 1024, // 100MB
      model: 50 * 1024 * 1024, // 50MB for 3D models (GLB)
      wallVideo: 15 * 1024 * 1024, // 15MB - for wall video loops
      wallImage: 10 * 1024 * 1024, // 10MB - for wall images
      mp3: 15 * 1024 * 1024, // 15MB - for object music
    },
    maxFilesPerRoom: 50,
    maxStoragePerRoom: 500 * 1024 * 1024 // 500MB
  },

  // Feature flags
  features: {
    enableDebugLogs: NODE_ENV === 'development',
    enablePerformanceMonitoring: REACT_APP_STAGE !== 'dev',
    enableErrorReporting: NODE_ENV === 'production',
    enableAssetPreloading: true,
    enableAssetCaching: true,
    // Per-room owner Stats + room Rename are WIP/buggy (rename can break rooms),
    // so they're hidden on prod for now and only available on dev/personal.
    enableRoomStatsAndRename: REACT_APP_STAGE !== 'prod' && REACT_APP_STAGE !== 'production'
  },

  // Performance
  performance: {
    assetLoadBatchSize: 5,
    assetCacheCleanupInterval: 10 * 60 * 1000, // 10 minutes
    assetCacheMaxAge: 30 * 60 * 1000, // 30 minutes
    apiRetryDelay: 1000 // 1 second
  }
};

// Debug logging in development
if (config.features.enableDebugLogs) {
  console.log('🔧 Environment Configuration:', {
    NODE_ENV,
    REACT_APP_STAGE,
    apiBaseUrl: config.api.baseUrl,
    s3BaseUrl: config.assets.s3BaseUrl,
    features: config.features
  });
}

export default config;

// Extract AWS region from API Gateway URL
export const getAwsRegion = () => {
  const url = getApiBaseUrl();
  const match = url.match(/\.execute-api\.([^.]+)\.amazonaws\.com/);
  return match ? match[1] : 'us-east-2';
};
